<template>
  <Layout>
    <div class="medical-qa-container">
      <!-- Header with model selection -->
      <div class="header">
        <el-button type="primary" class="link-button" @click="linkData">
          Connect Your Data
        </el-button>
      </div>

      <!-- Chat messages container -->
      <div ref="messagesContainer" class="messages-container">
        <div v-for="(message, index) in conversationList" :key="index">
          <chat-message
            :message="message"
            :is-a-i="message.role === 'ai'"
            :ai-loading="aiLoading && index === conversationList.length - 1"
            @report="handleReport()"
          />
        </div>
      </div>

      <!-- Input area -->
      <div class="input-container">
        <el-input
          v-model="question"
          placeholder="Ask a question"
          class="question-input"
          @keyup.enter="sendQuestion"
        ></el-input>
        <el-button
          type="primary"
          class="send-button"
          :disabled="!agreed || aiLoading || !raStatus"
          @click="sendQuestion"
        >
          Ask
        </el-button>
        <el-button
          type="text"
          class="clear-button"
          :disabled="!agreed || aiLoading || !raStatus"
          @click="clearChatHistory"
        >
          Clear
        </el-button>
      </div>

      <!-- Terms checkbox -->
      <div class="terms-container">
        <el-checkbox v-model="agreed">
          I agree to the
          <el-link type="primary" @click="termsDialogVisible = true">
            Terms & Conditions
          </el-link>
        </el-checkbox>
      </div>

      <!-- Terms dialog -->
      <el-dialog
        title="Terms & Conditions"
        :visible.sync="termsDialogVisible"
        width="70%"
      >
        <div class="terms-content">
          <p>
            Lavita's mission is to help individuals take full control of their
            health data. Users will benefit by unlocking the value of their
            health data, to receive personalized health and wellness
            information, and to participate in paying research and studies.
          </p>

          <p>
            By agreeing to this consent, you give permission to Lavita to
            retrieve and encrypt your health data from Epic and other EHR
            providers. Your data will be immediately encrypted on your device
            browser, using a private encryption key that is generated from your
            chosen password.
          </p>

          <p>
            Lavita never stores your unencrypted data. Only users themselves can
            access their data by login into Lavita platform and using their
            private encryption key, generated by your password. No entity,
            including Lavita itself, can decrypt your data without your own
            explicit consent.
          </p>

          <p>
            If you choose to contribute your data to Lavita platform, Lavita
            will collect certain de-identified meta data about you to allow
            researchers to ask for your participation, with your permission, in
            paid studies through Lavita platform.
          </p>

          <p>For further information please contact privacy@lavita.ai</p>
        </div>
      </el-dialog>
    </div>
  </Layout>
</template>

<script>
import CryptoJS from "crypto-js";
import { v4 as uuidv4 } from "uuid";
import ChatMessage from "./ChatMessage.vue";
import api from "@/http/api";
import Layout from "@/components/Partials/Layout";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import handleMetaData from "@/lib/dataProcess";
import generateMedicalSummary from "@/lib/ehrDataSummary";

export default {
  components: {
    ChatMessage,
    Layout,
  },
  data() {
    return {
      // Chat state
      question: "",
      conversationList: [],
      aiLoading: false,
      uuid: uuidv4(),
      raStatus: false,
      agreed: false,

      // Dialogs
      termsDialogVisible: false,
      downlodDataDialogVisible: false,

      // Data connection
      ehrDataList: [],
      selectedEhrDataList: {},

      // Decryption
      decryptForm: {
        phrase: "",
        verificationCode: "",
        encryptedData: "",
        decryptedData: "",
        resultData: "",
        originalData: null,
      },

      // Model selection
      modelList: [
        {
          label: "Llama-3.1-Nemotron-70B-instruct",
          value: "1",
          description: "100% secure, more powerful",
        },
        {
          label: "Gemma-2-27B-it",
          value: "2",
          description: "100% secure, faster",
        },
      ],
      selectedModel: {
        label: "Llama-3.1-Nemotron-70B-instruct",
        value: "1",
        description: "100% secure, more powerful",
      },

      // Streaming
      currentRequestID: null,
      storedPoll: null,
      queuedConversationId: null,
      // 一旦上传过图片，那每次对话都要带上imageFile这个参数，所以要存起来
      uploadedImageFile: "",
      // 一旦上传过EHR数据，那每次对话都要带上ehrData这个参数，所以要存起来
      uploadedEhrData: false,
      // 一旦上传过PDF数据，那每次对话都要带上pdfData这个参数，所以要存起来
      uploadedPdfData: false,
      // 一旦上传过图片数据，那每次对话都要带上imageData这个参数，所以要存起来
      uploadedImageData: false,
    };
  },
  mounted() {
    this.init();
  },
  methods: {
    async init() {
      const loading = this.$loading({
        lock: true,
        text: "Loading...",
        spinner: "el-icon-loading",
        background: "rgba(0, 0, 0, 0.8)",
      });
      try {
        await api.raSystem({
          userId: this.$store.state.userInfo.userId,
        });
        this.raStatus = true;

        this.conversationList = [
          {
            role: "ai",
            content: `Our **Secure AI Companion** is powered by Google's latest open-source language model, **Gemma-3**. It offers strong natural language understanding and generation capabilities. The model is deployed in Azure confidential VMs with NVIDIA H100 Tensor Core GPUs. It combines the hardware-based data-in-use protection capabilities of 4th generation AMD EPYCTM processor-based confidential VMs with the performance of NVIDIA H100 Tensor Core GPUs.

For more technical details, please refer to the following resources (links will open in a new page):

- [General Availability: Azure confidential VMs with NVIDIA H100 Tensor Core GPUs](https://techcommunity.microsoft.com/blog/azureconfidentialcomputingblog/general-availability-azure-confidential-vms-with-nvidia-h100-tensor-core-gpus/4242644)
- [Gemma-3 Research Paper (arXiv)](https://arxiv.org/pdf/2503.19786)
- [Google Blog: Introducing Gemma-3](https://blog.google/technology/developers/gemma-3/)`,
          },
          {
            role: "ai",
            content: `### Hi! This is **Lavita Secure AI Companion**

You can consult with me on medical and health-related questions and receive personalized advice.  
I'm here to support your well-being by providing helpful information tailored to your needs.

### **Important Notice**

This app is powered by AI and is **not a substitute for professional medical advice, diagnosis, or treatment**.

All responses are for **informational purposes only** and should **not** be used as medical guidance.  
If you have any health concerns, please consult with a qualified healthcare provider.`,
          },
          {
            role: "ai",
            content: `Remote attestation is successful! Your conversation utilizes end-to-end encryption. Your data is 100% secure and private, Lavita itself cannot access your data plain text.`,
          },
        ];
        this.scrollToBottom();
        if (Object.keys(this.$route.params).length > 0) {
          this.importData();
        }
      } catch (error) {
        console.error(error);
        this.conversationList = [
          {
            role: "ai",
            content: `Remote attestation failed, please exit the page and try again`,
          },
        ];
      } finally {
        loading.close();
      }
    },

    clearChatHistory() {
      this.uuid = uuidv4();
      this.selectedEhrDataList = {};
      this.uploadedImageFile = "";
      this.uploadedEhrData = false;
      this.init();
    },

    linkData() {
      const info = this.$store.state.subscriptionInfo;
      let parsed = info;
      if (typeof info === "string") {
        try { parsed = JSON.parse(info); } catch { parsed = null; }
      }
      const entitlements = parsed?.subscriber?.entitlements || {};
      const now = new Date();
      const isPro = Object.values(entitlements).some(
        (e) => e && new Date(e.expires_date) > now
      );
      if (!isPro) {
        this.$alert(
          "Connecting your health data requires a Plus or Pro subscription. Please upgrade via the Lavita mobile app to unlock this feature.",
          "Subscription Required",
          {
            confirmButtonText: "OK",
            type: "warning",
          }
        );
        return;
      }
      // Navigate to data selection page
      this.$router.push({ name: "MyContributions", query: { aiAssist: true } });
    },

    async sendQuestion() {
      if (!this.question.trim()) return;

      try {
        this.aiLoading = true;
        const question = this.question;
        this.question = "";

        this.conversationList.push({
          role: "user",
          content: question,
        });

        this.scrollToBottom();

        const requestId = uuidv4();
        this.currentRequestID = requestId;
        const BearerToken = sessionStorage.getItem("BearerToken");

        const params = {
          input: {
            human_input: `${question}`,
          },
          config: { configurable: { conversation_id: this.uuid } },
          model_id: this.selectedModel.value,
          user_id: this.$store.state.userInfo.userId,
          auth_token: BearerToken,
          userType: 1,
          requestId,
          imageFile: this.uploadedImageFile ? this.uploadedImageFile : null,
          ehrData: this.uploadedEhrData ? this.uploadedEhrData : null,
          pdfData: this.uploadedPdfData ? this.uploadedPdfData : null,
          imageData: this.uploadedImageData ? this.uploadedImageData : null,
          userTierInfo: this.buildUserTierInfo(),
        };

        // Add empty AI message for streaming
        this.conversationList.push({
          role: "ai",
          content: "",
        });

        const controller = new AbortController();

        fetchEventSource("https://gateway.lavita.ai/streaming-service/chat", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "text/event-stream, */*",
          },
          body: JSON.stringify(params),
          signal: controller.signal,

          onmessage: (event) => {
            if (
              this.isJsonObject(event.data) &&
              JSON.parse(event.data).status
            ) {
              this.pollQueue(JSON.parse(event.data));
              controller.abort();
              return;
            }
            const chunk = event.data;
            const cleanedData = chunk
              .replace(/^"/, "")
              .replace(/"$/, "")
              .replace(/\\n/g, "  \n");

            if (cleanedData.includes("run_id") || cleanedData.includes("[END]")) {
              this.aiLoading = false;
              return;
            }
            const lastIndex = this.conversationList.length - 1;
            if (this.conversationList[lastIndex].role === "ai") {
              // 直接修改对象属性，触发响应式更新
              this.conversationList[lastIndex].content += cleanedData;

              // 强制Vue更新DOM
              this.$forceUpdate();
              this.scrollToBottom();
            }
          },
          onclose: () => {
            this.aiLoading = false;
            this.$nextTick(() => {
              this.scrollToBottom();
            });
          },
          onerror: (error) => {
            console.error("SSE error:", error);
            controller.abort();
            this.aiLoading = false;
          },
        });
      } catch (error) {
        console.error(error);
        this.conversationList.push({
          role: "ai",
          content: "Something went wrong, please try again",
        });
        this.aiLoading = false;
      }
    },

    async pollQueue(data) {
      try {
        if (data.status) {
          const info = data;

          if (info.status === "queued") {
            this.queuedConversationId = info.conversation_id;

            let newQueueMessage = {
              queueMessage: true,
              role: "ai",
              content: "",
            };

            if (info.estimated_wait_time <= 90) {
              newQueueMessage.content =
                "Your query is being processed through Lavita's Confidential Computing pipeline. Please wait ...";
            } else if (info.estimated_wait_time <= 180) {
              newQueueMessage.content =
                "Your query is being processed, however it may take a couple of minutes to complete your request through Lavita's Confidential Computing pipeline...";
            } else {
              newQueueMessage.content =
                "Lavita's Confidential Computing pipelines are busy with multiple user queries. Please resubmit your query again later.";
            }

            const lastItem =
              this.conversationList[this.conversationList.length - 1];

            if (lastItem && lastItem.queueMessage) {
              this.conversationList.splice(-1, 1, newQueueMessage);
            } else {
              this.conversationList.push(newQueueMessage);
            }

            if (info.estimated_wait_time > 180) return;
            if (this.storedPoll) return;

            this.storedPoll = setInterval(async () => {
              const res = await api.queueStatus({
                requestId: this.currentRequestID,
              });
              this.pollQueue(res.data);
            }, 10000);
          } else if (info.status === "processing") {
            const newProcessingMessage = {
              processingMessage: true,
              role: "ai",
              content: `Your request is currently being processed...`,
            };

            const lastItem =
              this.conversationList[this.conversationList.length - 1];

            if (lastItem && lastItem.processingMessage) {
              this.conversationList.splice(-1, 1, newProcessingMessage);
            } else {
              this.conversationList.push(newProcessingMessage);
            }
          } else {
            this.$message.error("Something went wrong, please try again");
            clearInterval(this.storedPoll);
            this.storedPoll = null;
          }
        } else if (typeof data === "string") {
          clearInterval(this.storedPoll);
          this.storedPoll = null;

          const firstChunkEnd = data.indexOf("}") + 1;
          let textPart = data.slice(firstChunkEnd).trim();
          const cleanedData = textPart
            .replace(/^"/, "")
            .replace(/"$/, "")
            .replace(/\\n/g, "  \n")
            .replace(/"([^"]+)"/g, (match, p1) => p1);

          this.conversationList.push({
            role: "ai",
            content: cleanedData,
          });
        } else {
          this.$message.error("Something went wrong, please try again");
          clearInterval(this.storedPoll);
          this.storedPoll = null;
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.scrollToBottom();
      }
    },

    isJsonObject(str) {
      try {
        const obj = JSON.parse(str);
        return typeof obj === "object" && obj !== null && !Array.isArray(obj);
      } catch (e) {
        return false;
      }
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },
    base64ToBlob(base64, contentType = "application/pdf") {
      const byteCharacters = atob(base64);
      const byteArrays = [];

      for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        const slice = byteCharacters.slice(offset, offset + 512);
        const byteNumbers = new Array(slice.length);

        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }

      return new Blob(byteArrays, { type: contentType });
    },
    dataURLToBlob(dataURL) {
      const matches = dataURL.match(/^data:(.*);base64,(.*)$/);
      if (!matches) throw new Error("Invalid data URL format");

      const contentType = matches[1];
      const base64Data = matches[2];
      const byteCharacters = atob(base64Data);

      const byteArrays = [];
      for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        const slice = byteCharacters.slice(offset, offset + 512);
        const byteNumbers = Array.from(slice).map((char) => char.charCodeAt(0));
        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }

      return new Blob(byteArrays, { type: contentType });
    },
    async importData() {
      const loading = this.$loading({
        lock: true,
        text: "Loading...",
        spinner: "el-icon-loading",
        background: "rgba(0, 0, 0, 0.8)",
      });
      try {
        console.log(this.$route.params);
        this.aiLoading = true;
        switch (this.$route.params.type) {
          case 1: {
            try {
              const resultData = handleMetaData(
                JSON.parse(this.$route.params.decryptedData)
              );
              // 删除resultData中的用户姓名
              resultData.patient.descriptionData.name = "Anonymous User";
              const summary = generateMedicalSummary(resultData);
              this.conversationList.push({
                role: "ai",
                content: `I'm connecting your data to the secure messaging channel. This may take a couple of minutes ...`,
              });

              this.scrollToBottom();

              const requestId = uuidv4();
              this.currentRequestID = requestId;
              const BearerToken = sessionStorage.getItem("BearerToken");

              const params = {
                input: {
                  human_input: `Below is the foundational background information for the following inquiries. I have some data points which contains my EHR data, please remember these data points, I may ask you some questions based on these data later, here's the data: ${summary}, just answer "I have imported your data, you can ask me questions about it now" once you finish`,
                },
                config: {
                  configurable: {
                    conversation_id: this.uuid,
                  },
                },
                model_id: this.selectedModel.value,
                user_id: this.$store.state.userInfo.userId,
                auth_token: BearerToken,
                userType: 1,
                requestId,
                ehrData: true,
                userTierInfo: this.buildUserTierInfo(),
              };
              // 一旦上传过EHR数据，那每次对话都要带上ehrData这个参数，所以要存起来
              this.uploadedEhrData = true;

              const controller = new AbortController();

              fetchEventSource(
                "https://gateway.lavita.ai/streaming-service/chat",
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Accept: "text/event-stream, */*",
                  },
                  body: JSON.stringify(params),
                  signal: controller.signal,

                  onmessage: (event) => {
                    if (
                      this.isJsonObject(event.data) &&
                      JSON.parse(event.data).status
                    ) {
                      this.pollQueue(JSON.parse(event.data));
                      controller.abort();
                      return;
                    }
                  },
                  onclose: () => {
                    this.conversationList.push({
                      role: "ai",
                      content: `I have imported your data, you can ask me questions about it now.`,
                    });
                    this.aiLoading = false;
                    this.$nextTick(() => {
                      this.scrollToBottom();
                    });
                  },
                  onerror: (error) => {
                    console.error("SSE error:", error);
                    controller.abort();
                    this.aiLoading = false;
                  },
                }
              );
            } catch (error) {
              console.error(error);
              this.$message.error("Something went wrong, please try again");
            }
            break;
          }
          case 2: {
            try {
              console.log(this.$route.params);
              const formData = new FormData();
              const blob = this.dataURLToBlob(this.$route.params.decryptedData);
              // 这里的文件名是User-id-fileName-时间戳的格式，大模型对名字有权重，只把fileName留下
              const str = this.$route.params.dataInfo.basicInfo.filename;
              const parts = str.split("-");
              const originalFileName = parts[parts.length - 2];
              formData.append("file", blob, originalFileName);
              const params = {
                userId: this.$route.params.dataInfo.basicInfo.userId,
                type: this.$route.params.dataInfo.basicInfo.type,
                subType: this.$route.params.dataInfo.basicInfo.subType,
                edgeNodeId: this.$route.params.dataInfo.basicInfo.edgeNodeId,
                privacy: this.$route.params.dataInfo.basicInfo.privacy,
                policyAgreed: true,
                agreed: false,
                selectedOrg: "",
                plainImage: 1,
              };
              const res = await api.uploadFileTest(formData, {
                userId: this.$store.state.userInfo.userId,
                ...params,
              });
              // 一旦上传过图片，那每次对话都要带上imageFile这个参数，所以要存起来
              this.uploadedImageFile = res.data.data;
              this.conversationList.push({
                role: "ai",
                content: `I'm connecting your data to the secure messaging channel. This may take a couple of minutes ...`,
              });

              this.scrollToBottom();

              const requestId = uuidv4();
              this.currentRequestID = requestId;
              const BearerToken = sessionStorage.getItem("BearerToken");

              const chatParams = {
                input: {
                  human_input: `Below is the foundational background information for the following inquiries. Answer my question based on the image I just sent you, here are some metadata about the image: ${this.$route.params.dataInfo.basicInfo.metaData}, just answer "I have imported your data, you can ask me questions about it now" once you finish`,
                },
                config: {
                  configurable: {
                    conversation_id: this.uuid,
                  },
                },
                model_id: this.selectedModel.value,
                user_id: this.$store.state.userInfo.userId,
                auth_token: BearerToken,
                userType: 1,
                requestId,
                imageFile: res.data.data,
                imageData: true,
                userTierInfo: this.buildUserTierInfo(),
              };

              const controller = new AbortController();

              fetchEventSource(
                "https://gateway.lavita.ai/streaming-service/chat",
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Accept: "text/event-stream, */*",
                  },
                  body: JSON.stringify(chatParams),
                  signal: controller.signal,

                  onmessage: (event) => {
                    if (
                      this.isJsonObject(event.data) &&
                      JSON.parse(event.data).status
                    ) {
                      this.pollQueue(JSON.parse(event.data));
                      controller.abort();
                      return;
                    }
                  },
                  onclose: () => {
                    this.conversationList.push({
                      role: "ai",
                      content: `I have imported your data, you can ask me questions about it now.`,
                    });
                    // ✅ 流自然终止，清理 loading 状态等
                    this.aiLoading = false;
                    this.$nextTick(() => {
                      this.scrollToBottom();
                    });
                  },
                  onerror: (error) => {
                    console.error("SSE error:", error);
                    controller.abort();
                    this.aiLoading = false;
                  },
                }
              );
            } catch (error) {
              console.error(error);
              this.$message.error("Something went wrong, please try again");
            }
            break;
          }
          case 5: {
            try {
              const blob = this.base64ToBlob(this.$route.params.decryptedData);
              const formData = new FormData();
              formData.append("file", blob, "temp.pdf");
              const pdfToMarkdownRes = await api.pdfToMarkdown(formData);
              const summary = pdfToMarkdownRes?.data?.markdown;
              this.conversationList.push({
                role: "ai",
                content: `I'm connecting your data to the secure messaging channel. This may take a couple of minutes ...`,
              });

              this.scrollToBottom();

              const requestId = uuidv4();
              this.currentRequestID = requestId;
              const BearerToken = sessionStorage.getItem("BearerToken");

              const params = {
                input: {
                  human_input: `Below is the foundational background information for the following inquiries. I have some data points which contains my data, please remember these data points, I may ask you some questions based on these data later, here's the data: ${summary}, just answer "I have imported your data, you can ask me questions about it now" once you finish`,
                },
                config: {
                  configurable: {
                    conversation_id: this.uuid,
                  },
                },
                model_id: this.selectedModel.value,
                user_id: this.$store.state.userInfo.userId,
                auth_token: BearerToken,
                userType: 1,
                requestId,
                pdfData: true,
                userTierInfo: this.buildUserTierInfo(),
              };

              const controller = new AbortController();

              fetchEventSource(
                "https://gateway.lavita.ai/streaming-service/chat",
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Accept: "text/event-stream, */*",
                  },
                  body: JSON.stringify(params),
                  signal: controller.signal,

                  onmessage: (event) => {
                    if (
                      this.isJsonObject(event.data) &&
                      JSON.parse(event.data).status
                    ) {
                      this.pollQueue(JSON.parse(event.data));
                      controller.abort();
                      return;
                    }
                  },
                  onclose: () => {
                    this.conversationList.push({
                      role: "ai",
                      content: `I have imported your data, you can ask me questions about it now.`,
                    });
                    // ✅ 流自然终止，清理 loading 状态等
                    this.aiLoading = false;
                    this.$nextTick(() => {
                      this.scrollToBottom();
                    });
                  },
                  onerror: (error) => {
                    console.error("SSE error:", error);
                    controller.abort();
                    this.aiLoading = false;
                  },
                }
              );
            } catch (error) {
              console.error(error);
              this.$message.error("Something went wrong, please try again");
            }
            break;
          }
        }
      } catch (error) {
        console.error(error);
        this.$message.error("Something went wrong, please try again");
      } finally {
        // this.aiLoading = false;
        loading.close();
      }
    },

    // 将 web 端 REST API 格式的 subscriptionInfo 转换为 app 端 SDK 格式
    buildUserTierInfo() {
      const info = this.$store.state.subscriptionInfo;
      if (!info) return null;
      let parsed = info;
      if (typeof info === "string") {
        try {
          parsed = JSON.parse(info);
        } catch {
          return null;
        }
      }
      const subscriber = parsed?.subscriber;
      if (!subscriber) return null;
      const now = new Date();

      // 构建 entitlements.all
      const allEntitlements = {};
      Object.entries(subscriber.entitlements || {}).forEach(([key, val]) => {
        const isActive = val?.expires_date && new Date(val.expires_date) > now;
        allEntitlements[key] = {
          identifier: key,
          isActive: !!isActive,
          productIdentifier: val.product_identifier,
          productPlanIdentifier: null,
          expirationDate: val.expires_date || null,
          expirationDateMillis: val.expires_date ? new Date(val.expires_date).getTime() : null,
          latestPurchaseDate: val.purchase_date || null,
          latestPurchaseDateMillis: val.purchase_date ? new Date(val.purchase_date).getTime() : null,
          originalPurchaseDate: val.purchase_date || null,
          originalPurchaseDateMillis: val.purchase_date ? new Date(val.purchase_date).getTime() : null,
          store: "APP_STORE",
          periodType: "NORMAL",
          ownershipType: "PURCHASED",
          willRenew: true,
          unsubscribeDetectedAt: null,
          unsubscribeDetectedAtMillis: null,
          billingIssueDetectedAt: null,
          billingIssueDetectedAtMillis: null,
          isSandbox: false,
          verification: "NOT_REQUESTED",
        };
      });

      // 构建 entitlements.active
      const activeEntitlements = {};
      Object.entries(allEntitlements).forEach(([key, val]) => {
        if (val.isActive) activeEntitlements[key] = val;
      });

      // 构建 subscriptionsByProductIdentifier
      const subscriptionsByProductIdentifier = {};
      Object.entries(subscriber.subscriptions || {}).forEach(([key, val]) => {
        const isActive = val?.expires_date && new Date(val.expires_date) > now;
        subscriptionsByProductIdentifier[key] = {
          productIdentifier: key,
          isActive: !!isActive,
          expiresDate: val.expires_date || null,
          purchaseDate: val.purchase_date || null,
          originalPurchaseDate: val.original_purchase_date || null,
          store: (val.store || "APP_STORE").toUpperCase(),
          periodType: (val.period_type || "normal").toUpperCase(),
          ownershipType: val.ownership_type || "PURCHASED",
          willRenew: val.will_renew !== undefined ? val.will_renew : true,
          isSandbox: val.is_sandbox || false,
          gracePeriodExpiresDate: val.grace_period_expires_date || null,
          unsubscribeDetectedAt: val.unsubscribe_detected_at || null,
          refundedAt: val.refunded_at || null,
          billingIssuesDetectedAt: val.billing_issues_detected_at || null,
          storeTransactionId: val.store_transaction_id || null,
          price: val.price || null,
        };
      });

      // 计算 allExpirationDates / allPurchaseDates
      const allExpirationDates = {};
      const allPurchaseDates = {};
      const allExpirationDatesMillis = {};
      const allPurchaseDatesMillis = {};
      Object.entries(subscriber.subscriptions || {}).forEach(([key, val]) => {
        if (val.expires_date) {
          allExpirationDates[key] = val.expires_date;
          allExpirationDatesMillis[key] = new Date(val.expires_date).getTime();
        }
        if (val.purchase_date) {
          allPurchaseDates[key] = val.purchase_date;
          allPurchaseDatesMillis[key] = new Date(val.purchase_date).getTime();
        }
      });

      const activeSubscriptions = Object.values(activeEntitlements).map(
        (e) => e.productIdentifier
      );

      const latestExpirationDate =
        Object.values(allExpirationDates).sort().pop() || null;
      const latestExpirationDateMillis = latestExpirationDate
        ? new Date(latestExpirationDate).getTime()
        : null;

      const sdkFormat = {
        originalAppUserId: subscriber.original_app_user_id,
        originalApplicationVersion: subscriber.original_application_version || "1.0",
        originalPurchaseDate: subscriber.original_purchase_date || null,
        originalPurchaseDateMillis: subscriber.original_purchase_date
          ? new Date(subscriber.original_purchase_date).getTime()
          : null,
        firstSeen: subscriber.first_seen || null,
        firstSeenMillis: subscriber.first_seen
          ? new Date(subscriber.first_seen).getTime()
          : null,
        requestDate: parsed.request_date || null,
        requestDateMillis: parsed.request_date_ms || null,
        managementURL: subscriber.management_url || null,
        entitlements: {
          all: allEntitlements,
          active: activeEntitlements,
          verification: "NOT_REQUESTED",
        },
        subscriptionsByProductIdentifier,
        activeSubscriptions,
        allPurchasedProductIdentifiers: Object.keys(subscriptionsByProductIdentifier),
        nonSubscriptionTransactions: [],
        allExpirationDates,
        allExpirationDatesMillis,
        allPurchaseDates,
        allPurchaseDatesMillis,
        latestExpirationDate,
        latestExpirationDateMillis,
      };
      return JSON.stringify(sdkFormat);
    },

    handleMetaData(data) {
      // Implement your metadata handling logic
      return data;
    },

    generateMedicalSummary(data) {
      // Implement your medical summary generation logic
      return JSON.stringify(data);
    },

    handleReport() {
      // Implement report functionality
      this.$prompt("Please describe the issue", "Report Message", {
        confirmButtonText: "Submit",
        cancelButtonText: "Cancel",
        inputType: "textarea",
      })
        .then(async ({ value }) => {
          try {
            const formData = new FormData();
            formData.append("file", "");

            await api.uploadFeedback(formData, {
              userId: this.$store.state.userInfo.userId,
              subject: `Ai assist report question`,
              description: value,
            });

            this.$message.success("Reported successfully");
          } catch (error) {
            console.error(error);
            this.$message.error("Failed to report");
          }
        })
        .catch(() => {
          // Cancel
        });
    },
  },
};
</script>

<style scoped>
.medical-qa-container {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 130px);
  background-color: #fff;
}

.header {
  text-align: end;
  padding: 20px;
}

.link-button {
  margin: 15px;
}

.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.input-container {
  display: flex;
  align-items: center;
  padding: 16px;
  border-top: 1px solid #eee;
  background-color: #fff;
}

.question-input {
  flex: 1;
  margin-right: 12px;
}

.send-button {
  margin-left: 12px;
}

.clear-button {
  margin-left: 12px;
  color: #f56c6c;
}

.terms-container {
  display: flex;
  align-items: center;
  padding: 0 16px 16px;
}

.decryption-dialog {
  padding: 20px;
}

.decrypt-button {
  width: 100%;
  margin-top: 20px;
}

.biometric-button {
  display: flex;
  align-items: center;
  justify-content: center;
}

.biometric-button i {
  margin-right: 8px;
  font-size: 18px;
}

.terms-content p {
  margin-bottom: 16px;
  line-height: 1.6;
}
</style>
